import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Typography } from '@/components';
import { Card } from '@/components/ui/card';
import { useNotifications } from '@/features/notifications';

import { useUpdateCompanyMutation } from '../../api';
import { CompanySchema } from '../../types';
import { AboutDrawer } from './about-drawer';
import { AboutFormFields } from './about-form';

const FORM_ID = 'company-about-form';

export type AboutCardProps = {
  readonly company: CompanySchema;
};

export function AboutCard({ company }: AboutCardProps) {
  const { t } = useTranslation(['settings']);
  const [editing, setEditing] = useState(false);
  const [updateCompany, { isLoading }] = useUpdateCompanyMutation();
  const { add } = useNotifications();

  const onSubmit = (data: AboutFormFields) => {
    updateCompany({ id: company.id, data: { description: data.description } })
      .unwrap()
      .then(() => {
        add({
          id: new Date().toString(),
          title: t('Company updated'),
          description: t('Your company has been updated'),
        });
        setEditing(false);
      })
      .catch(() => {
        add({
          id: new Date().toString(),
          title: t('ERROR'),
          description: t('There was an error updating your company. Please try again later.'),
        });
      });
  };

  const paragraphs = (company.description ?? '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <>
      <Card className="p-6 gap-3">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <Typography as="h3" color="primary" size="title" weight="semibold">
            {t('About')}
          </Typography>
          {paragraphs.length ? (
            <Button size="sm" color="neutral" icon="edit" onClick={() => setEditing(true)}>
              {t('Edit')}
            </Button>
          ) : null}
        </div>

        {paragraphs.length ? (
          <div className="space-y-2">
            {paragraphs.map((paragraph, index) => (
              <Typography key={index} as="p" size="body-2" color="primary" className="max-w-3xl">
                {paragraph}
              </Typography>
            ))}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-primary-700 block text-left text-base font-medium hover:underline"
          >
            {t('Add a description of your company')}
          </button>
        )}
      </Card>

      <AboutDrawer
        formId={FORM_ID}
        company={company}
        isOpen={editing}
        isLoading={isLoading}
        onSubmit={onSubmit}
        onClose={() => setEditing(false)}
      />
    </>
  );
}
